/**
 * Patients and their care team.
 *
 * Replaces the patient half of `api/sessionApi.js` and `api/userApi.js`, plus
 * every inline `Patients` / `GetPatientInfo` / `AddPatientInfo` /
 * `UpdatePatientData` envelope posted to `tswebhook.php`.
 *
 * Nothing here sends an account number or a unique id. The server derives the
 * organization from the token and refuses anything outside it — which is why
 * `getPatient` on another tenant's id returns 404 rather than 403: confirming
 * the record exists elsewhere would itself be a disclosure.
 */

import api from './client';

// --- patients --------------------------------------------------------------

export function listPatients(params = {}) {
  return api.list('/patients', params);
}

export function searchPatients(term, params = {}) {
  return api.list('/patients', { 'filter[search]': term, ...params });
}

export function getPatient(patientId, params = {}) {
  return api.get(`/patients/${patientId}`, params);
}

export function createPatient(payload) {
  return api.post('/patients', payload);
}

export function updatePatient(patientId, patch) {
  return api.patch(`/patients/${patientId}`, patch);
}

/** Invite the patient to their own portal account. */
export function invitePatient(patientId, payload = {}) {
  return api.post(`/patients/${patientId}/invitation`, payload);
}

export function revokePatientInvitation(patientId) {
  return api.delete(`/patients/${patientId}/invitation`);
}

/** Share a patient's record with another organization by external id. */
export function sharePatient(patientId, payload) {
  return api.post(`/patients/${patientId}/shares`, payload);
}

// --- relationships ---------------------------------------------------------

export function listRelationships(patientId, params = {}) {
  return api.list(`/patients/${patientId}/relationships`, params);
}

export function getRelationship(patientId, relationshipId) {
  return api.get(`/patients/${patientId}/relationships/${relationshipId}`);
}

export function getRelationshipPermissions(patientId, relationshipId) {
  return api.get(`/patients/${patientId}/relationships/${relationshipId}/permissions`);
}

export function updateRelationship(patientId, relationshipId, payload) {
  return api.put(`/patients/${patientId}/relationships/${relationshipId}`, payload);
}

// --- insurance -------------------------------------------------------------

export function listInsurancePolicies(patientId) {
  return api.list(`/patients/${patientId}/insurance-policies`);
}

export function addInsurancePolicy(patientId, payload) {
  return api.post(`/patients/${patientId}/insurance-policies`, payload);
}

// --- care team -------------------------------------------------------------
//
// One endpoint family replaces the six the legacy API had — separate methods
// for assigning nurses, providers, aides and allied professionals, each with
// its own payload shape. `role` is the discriminator now.

export function getCareTeam(patientId, params = {}) {
  return api.get(`/patients/${patientId}/care-team`, params);
}

/** Replace the whole set of caregivers holding a role. */
export function setCareTeamRole(patientId, role, userIds) {
  return api.put(`/patients/${patientId}/care-team/${role}`, { user_ids: userIds });
}

/** Add one caregiver in a role, leaving the rest alone. */
export function addCareTeamMember(patientId, role, payload) {
  return api.post(`/patients/${patientId}/care-team/${role}`, payload);
}

export function updateCareTeamMember(patientId, memberId, patch) {
  return api.patch(`/patients/${patientId}/care-team/${memberId}`, patch);
}

export function removeCareTeamMember(patientId, role, userId) {
  return api.delete(`/patients/${patientId}/care-team/${role}/${userId}`);
}

export default {
  listPatients, searchPatients, getPatient, createPatient, updatePatient,
  invitePatient, revokePatientInvitation, sharePatient,
  listRelationships, getRelationship, getRelationshipPermissions, updateRelationship,
  listInsurancePolicies, addInsurancePolicy,
  getCareTeam, setCareTeamRole, addCareTeamMember, updateCareTeamMember, removeCareTeamMember,
};
